import React, { useEffect, useState } from "react";
import { useTable, useSortBy, usePagination } from "react-table";
import { PrevBtn, NextBtn, PaginationWrapper, MenuOptions, MenuItem, Btn } from "../Styles/PageStyles";
import Images from "../../Images";
import tw from "tailwind-styled-components";
import { PaginationLimit } from "../../Config";
import { capitalizeFirstLetter, truncateText } from "../../helpers";
import Tooltip from "../Tooltip";
import { FaFemale, FaMale } from "react-icons/fa";
import { BiMaleFemale } from "react-icons/bi";
import { GoArrowSwitch } from "react-icons/go";
import { IoEye } from "react-icons/io5";

const TableNew = ({
    apiData,
    setViewDetails,
    setSelectedData,
    setIsSwitchStamp
}) => {

    const storageKey = `tenant_active_page`;
    const [data, setData] = useState([]);
    const [selectedRow, setSelectedRow] = useState(null);
    const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);
    const [openActionMenu, setOpenActionMenu] = useState(null);
    const [previousId, setPreviousId] = useState(null);

    const toggleRow = (index) => {
        setSelectedRow(selectedRow === index ? null : index);
    };

    // Load saved page index
    const storageValue = sessionStorage.getItem(storageKey);
    const savedPage = storageValue !== null ? Number(storageValue) : 0;
    const [currentPage, setCurrentPage] = useState(0);

    const switchStamp = (item) => {
        setIsSwitchStamp(true);
        setSelectedData(item);
        setOpenActionMenu(null);
    };

    const view = (item) => {
        setViewDetails(true);
        setSelectedData(item);
        setOpenActionMenu(null);
    };

    const ActionBtns = (item) => {
        return (
            <div className="flex items-center gap-3">
                <Tooltip label={
                    item.available > 0
                        ? "Click to switch stamp"
                        : "No stamps available"
                }>
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            if (item.available <= 0) return;
                            switchStamp(item);
                        }}
                        className={`transition ${item.available > 0
                            ? "text-gray-400 hover:text-blue-600 cursor-pointer"
                            : "text-gray-400 cursor-not-allowed opacity-70"
                            }`}
                    >
                        <GoArrowSwitch size={18} />
                    </button>
                </Tooltip>
                <Tooltip label="Click to view details">
                    <button
                        onClick={() => view(item)}
                        className="p-2 rounded-full hover:bg-gray-100 transition cursor-pointer"
                    >
                        <IoEye className="text-gray-400" size={18} />
                    </button>
                </Tooltip>

            </div>
        );
    };

    useEffect(() => {
        if (apiData && apiData.length > 0) {
            const tempData = apiData.map((item) => ({
                firstParty: getFirstParty(item),
                property: getName(item),
                available: getAvailable(item),
                used: getUsed(item),
                action: ActionBtns(item),
                rawData: item
            }));
            setData(tempData);
        } else if (apiData && apiData.length === 0) {
            setData([]);
        }
    }, [apiData, openActionMenu]);

    const getName = (item) => (
        <UserImageWrapper>
            {/* Preference icon */}
            <div className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100">
                {item?.tenantPreference === 1 ? (
                    <FaMale className="text-blue-400" />
                ) : item.tenantPreference === 2 ? (
                    <FaFemale className="text-pink-400" />
                ) : (
                    <BiMaleFemale className="text-purple-400" />
                )}
            </div>

            <div className="flex flex-col min-w-0">
                <div className="flex items-center gap-2 group cursor-pointer">
                    <span className="font-medium text-gray-700 truncate">
                        {capitalizeFirstLetter(truncateText(item.propName, 28))}
                    </span>
                </div>

                <span className="text-xs text-gray-400">{item?.streetAddress || "NA"}</span>
            </div>
        </UserImageWrapper>
    );

    const getAvailable = (item) => {
        const count = Number(item.available);
        const isZero = count === 0;

        return (
            <div className={`inline-flex items-center px-3 py-1.5 rounded-full border font-semibold text-sm min-w-fit ${isZero ? "bg-red-50 border-red-100 text-red-700" : "bg-emerald-50 border-emerald-100 text-emerald-700"}`}>
                <span className={`h-2 w-2 rounded-full mr-2 ${isZero ? "bg-red-500" : "bg-emerald-500"}`} />
                {count}
            </div>
        );
    };

    const getUsed = (item) => {
        const total = (Number(item.available) || 0) + (Number(item.used) || 0);

        const usedPercentage =
            total > 0 ? (Number(item.used) / total) * 100 : 0;

        return (
            <div className="w-[140px]">
                <div className="flex justify-between mb-1">
                    <span className="text-xs font-medium text-gray-500">
                        Used
                    </span>

                    <span className="text-xs font-semibold text-gray-700">
                        {item.used}/{total}
                    </span>
                </div>

                <div className="h-2 w-full rounded-full bg-gray-100 overflow-hidden">
                    <div
                        className="
                            h-full
                            rounded-full
                            bg-gradient-to-r
                            from-blue-500
                            to-indigo-500
                            transition-all
                            duration-500
                        "
                        style={{
                            width: `${usedPercentage}%`,
                        }}
                    />
                </div>
            </div>
        );
    }

    const getFirstParty = (item) => {
        const name = capitalizeFirstLetter(
            item?.firstPartyName || "Unknown"
        );
        return (
            <div className="flex items-center gap-3 min-w-[150px]">
                <div className="flex flex-col min-w-0">
                    <span className="text-sm font-semibold text-gray-800 truncate">
                        {truncateText(name, 28)}
                    </span>

                    <span className="text-xs text-gray-400">
                        First Party
                    </span>
                </div>
            </div>
        );
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                !event.target.closest("#menuOptions") &&
                !event.target.closest(".action-btn")
            ) {
                setOpenActionMenu(null);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    useEffect(() => {
        const handleResize = () => {
            setIsMobileView(window.innerWidth < 768);
        };

        window.addEventListener("resize", handleResize);
        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    const columns = React.useMemo(() => {
        if (isMobileView) {
            return [

                {
                    Header: "Property",
                    accessor: "property",
                },
                {
                    Header: "First Party",
                    accessor: "firstParty",
                },
            ];
        }

        return [

            {
                Header: "Property",
                accessor: "property",
            },

            {
                Header: "First Party",
                accessor: "firstParty",
            },

            {
                Header: "Available",
                accessor: "available",
            },

            {
                Header: "Used",
                accessor: "used",
            },
            {
                Header: "",
                accessor: "action",
            },
        ];
    }, [isMobileView]);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        pageOptions,
        page,
        state: { pageIndex },
        previousPage,
        nextPage,
        canPreviousPage,
        canNextPage,
    } = useTable(
        {
            data,
            columns,
            initialState: { pageIndex: currentPage, pageSize: PaginationLimit },
        },
        useSortBy,
        usePagination
    );

    useEffect(() => {
        setCurrentPage(pageIndex);
    }, [pageIndex]);

    return (
        <>
            <CustomTable {...getTableProps()}>
                <Thead>
                    {headerGroups.map((headerGroup, idx) => (
                        <Tr {...headerGroup.getHeaderGroupProps()} key={idx}>
                            {headerGroup.headers.map((column, colIdx) => (
                                <Th {...column.getHeaderProps(column.getSortByToggleProps())} key={colIdx}>
                                    {column.render("Header")}
                                    <span>
                                        {column.isSorted ? (
                                            <img
                                                src={Images.Arrow}
                                                alt="down arrow"
                                                className={`${column.isSortedDesc ? "-rotate-90" : "rotate-90"
                                                    } w-1.5 inline-block ml-1.5`}
                                            />
                                        ) : (
                                            ""
                                        )}
                                    </span>
                                </Th>
                            ))}
                        </Tr>
                    ))}
                </Thead>
                <Tbody {...getTableBodyProps()} hasdata={(apiData?.length > 0).toString()}>
                    {page.map((row, rowIndex) => {
                        prepareRow(row);
                        return (
                            <React.Fragment key={rowIndex}>
                                <Tr
                                    {...row.getRowProps({ style: { height: "80px" } })}
                                    hasdata={(apiData?.length > 0).toString()}
                                >
                                    {row.cells.map((cell, cellIndex) => {
                                        return (
                                            <Td
                                                {...cell.getCellProps()}
                                                onClick={() => {
                                                    if (
                                                        cellIndex === 0 ||
                                                        (cellIndex === 1 && window.innerWidth < 768)
                                                    ) {
                                                        toggleRow(rowIndex);
                                                    }
                                                }}
                                                style={{ maxWidth: "320px" }}
                                                key={cellIndex}
                                            >
                                                {cell.render("Cell")}
                                            </Td>
                                        );
                                    })}
                                </Tr>
                                {selectedRow === rowIndex && window.innerWidth < 768 && (
                                    <Tr key={`expanded-${rowIndex}`}>
                                        <Td colSpan={columns.length}>
                                            <ul className="flex flex-col gap-2">
                                                <li>
                                                    <strong>Available</strong>{" "}
                                                    {data[rowIndex]?.rawData?.available}
                                                </li>
                                                <li>
                                                    <strong>Used</strong>{" "}
                                                    {data[rowIndex]?.rawData?.used}
                                                </li>
                                                <li>
                                                    <strong>Action</strong>{" "}
                                                    {data[rowIndex]?.rawData?.action}
                                                </li>

                                            </ul>
                                        </Td>
                                    </Tr>
                                )}
                            </React.Fragment>
                        );
                    })}
                </Tbody>
            </CustomTable>
            {apiData?.length > 0 && (
                <PaginationWrapper>
                    <div className="px-2">
                        Page{" "}
                        <em>
                            {pageIndex + 1} of {pageOptions.length}
                        </em>
                    </div>

                    <div className="flex gap-1">
                        <PrevBtn onClick={() => previousPage()} disabled={!canPreviousPage}>
                            <img src={Images.Arrow} alt="arrow" className="w-2 opacity-75 " />
                        </PrevBtn>
                        <NextBtn onClick={() => nextPage()} disabled={!canNextPage}>
                            <img
                                src={Images.Arrow}
                                alt="arrow"
                                className="w-2 rotate-180 opacity-75"
                            />
                        </NextBtn>
                    </div>
                </PaginationWrapper>
            )}
        </>
    );
};


const CustomTable = tw.table` w-full text-gray-600 `;
const Thead = tw.thead`border-b uppercase text-gray-600 bg-blue-50`;
const Tbody = tw.tbody`${(props) => (props.hasdata === "true" ? "border-b" : "")}`;
const Tr = tw.tr`${(props) =>
    props.hasdata === "true"
        ? "border-b"
        : ""} rounded-md overflow-hidden hover:bg-slate-50`;
const Th = tw.th`text-left text-sm md:p-3 p-0.5 font-normal text-gray-600`;
const Td = tw.td`p-3 text-sm md:p-3 text-left`;
const ActionBtnWrapper = tw.div`flex items-center gap-3 relative`;
const UserImageWrapper = tw.div` flex items-center gap-2`;

// const CustomTable = tw.table`
//     w-full
//     border-separate
//     border-spacing-y-2
// `;

// const Thead = tw.thead`
//     sticky
//     top-0
//     z-10
//     bg-white
// `;

// const Tbody = tw.tbody``;

// const TrHeader = tw.tr`
//     border-b
//     border-gray-100
// `;

// const Tr = tw.tr`
//     transition-all
//     duration-200
//     hover:shadow-md
//     hover:-translate-y-[1px]
//     hover:bg-blue-50/40
// `;

// const Th = tw.th`
//     text-left
//     text-[11px]
//     md:text-xs
//     font-semibold
//     tracking-wider
//     uppercase
//     text-gray-400
//     px-5
//     py-4
//     whitespace-nowrap
// `;

// const Td = tw.td`
//     px-5
//     py-4
//     text-sm
//     text-gray-700
//     font-medium
//     bg-white
//     first:rounded-l-2xl
//     last:rounded-r-2xl
// `;

// const ExpandedTd = tw.td`
//     px-4
//     py-2
//     bg-transparent
// `;
export default TableNew;
